import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Spinner } from 'reactstrap'

import { COLOURS, EVENT_TYPE, STRING } from 'resources'
import { APP_URLS } from 'resources/urls'

import { sendAnalyticsEvent } from 'services/analytics'
import { requestDataBrokerRemovalContract, deleteDataBrokerRemovalContract } from 'services/reklaimProtect'
import { formatDateWithSuffix } from 'utils/format-date'

import {
  Container,
  DetailContainer,
  SignStatusPill,
  CheckIcon,
  EditIcon,
  FileIcon,
  TitleContainer,
  Subtitle,
  Title,
  ButtonContainer,
  ArrowButton,
  VoidButton,
  InfoMessage,
} from './styles'

const MESSAGE_TYPE = {
  SUCCESS: 'success',
  ERROR: 'error',
}

const MESSAGE_INFO_COLOURS_MAP = {
  [MESSAGE_TYPE.SUCCESS]: COLOURS.systemGreen1,
  [MESSAGE_TYPE.ERROR]: COLOURS.secondaryRed1,
}

const DataBrokerRemovalContract = ({ hasCompletedProfile, contractInfo, setContractInfo, isContractInfoLoading }) => {
  const history = useHistory()
  const [loadVoid, setLoadVoid] = useState(false)
  const [loadSigning, setLoadSigning] = useState(false)
  const hasContract = !isContractInfoLoading && !!contractInfo?.signedAt
  const [infoMessage, setInfoMessage] = useState(() => {
    if (!hasCompletedProfile && !hasContract) {
      return {
        type: MESSAGE_TYPE.ERROR,
        message: STRING.protect.profileInfo.edit.dataBrokerRemovalContract.missingUserProfileError,
      }
    }

    return null
  })

  const voidContractInfo = async () => {
    setLoadVoid(true)
    sendAnalyticsEvent(EVENT_TYPE.protectVoidDataBrokerContract)
    try {
      const hasDeleted = await deleteDataBrokerRemovalContract()
      if (hasDeleted) {
        setContractInfo(null)
        setInfoMessage({
          type: MESSAGE_TYPE.SUCCESS,
          message: STRING.protect.profileInfo.edit.dataBrokerRemovalContract.voidSuccess,
        })
      } else {
        setInfoMessage({
          type: MESSAGE_TYPE.ERROR,
          message: STRING.protect.profileInfo.edit.dataBrokerRemovalContract.voidError,
        })
      }
    } catch (error) {
      setInfoMessage({
        type: MESSAGE_TYPE.ERROR,
        message: STRING.protect.profileInfo.edit.dataBrokerRemovalContract.voidError,
      })
    } finally {
      setLoadVoid(false)
    }
  }

  const signContract = async () => {
    setLoadSigning(true)
    sendAnalyticsEvent(EVENT_TYPE.protectSignDataBrokerContract)
    try {
      const response = await requestDataBrokerRemovalContract()
      if (response?.docusealSlug) {
        history.push({
          pathname: APP_URLS.PROTECT_SIGNING,
          state: {
            docusealSlug: response?.docusealSlug,
          },
        })
      } else {
        setInfoMessage({
          type: MESSAGE_TYPE.SUCCESS,
          message: STRING.protect.profileInfo.edit.dataBrokerRemovalContract.signError,
        })
      }
    } catch (error) {
      setInfoMessage({
        type: MESSAGE_TYPE.ERROR,
        message: STRING.protect.profileInfo.edit.dataBrokerRemovalContract.signError,
      })
    } finally {
      setLoadSigning(false)
    }
  }

  return (
    <Container>
      {isContractInfoLoading ? (
        <Spinner />
      ) : (
        <>
          <DetailContainer>
            <TitleContainer>
              {hasContract ? (
                <>
                  <SignStatusPill status="signed">
                    <CheckIcon />
                    {STRING.protect.profileInfo.edit.dataBrokerRemovalContract.signed}
                  </SignStatusPill>
                  <Subtitle>{formatDateWithSuffix(contractInfo.signedAt)}</Subtitle>
                </>
              ) : (
                <SignStatusPill status="unsigned">
                  <EditIcon />
                  {STRING.protect.profileInfo.edit.dataBrokerRemovalContract.unsigned}
                </SignStatusPill>
              )}
              <Title>{STRING.protect.profileInfo.edit.dataBrokerRemovalContract.title}</Title>
            </TitleContainer>
            <FileIcon />
          </DetailContainer>
          <ButtonContainer>
            {hasContract ? (
              <>
                <ArrowButton
                  withArrow
                  href={contractInfo.documentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  disabled={loadVoid}
                >
                  {STRING.protect.profileInfo.edit.dataBrokerRemovalContract.viewButton}
                </ArrowButton>
                <VoidButton onPress={voidContractInfo} isLoading={loadVoid} disabled={loadVoid}>
                  {STRING.protect.profileInfo.edit.dataBrokerRemovalContract.voidButton}
                </VoidButton>
              </>
            ) : (
              <ArrowButton onPress={signContract} isLoading={loadSigning} disabled={!hasCompletedProfile}>
                {STRING.protect.profileInfo.edit.dataBrokerRemovalContract.signButton}
              </ArrowButton>
            )}
          </ButtonContainer>
          {!!infoMessage && (
            <InfoMessage colour={MESSAGE_INFO_COLOURS_MAP[infoMessage.type]}>{infoMessage.message}</InfoMessage>
          )}
        </>
      )}
    </Container>
  )
}

DataBrokerRemovalContract.propTypes = {
  hasCompletedProfile: PropTypes.bool.isRequired,
  contractInfo: PropTypes.shape({
    createdAt: PropTypes.string,
    documentUrl: PropTypes.string,
    docusealSlug: PropTypes.string,
    docusealSubmissionId: PropTypes.string,
    docusealTemplateId: PropTypes.number,
    signedAt: PropTypes.string,
    userAlias: PropTypes.string,
  }),
  setContractInfo: PropTypes.func.isRequired,
  isContractInfoLoading: PropTypes.bool.isRequired,
}

export default DataBrokerRemovalContract
